import DashLayout from "@/layouts/DashLayout";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import PageTitle from "@/components/PageTitle";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { cn, isGranted } from "@/lib/utils";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { useShiftOrders } from "@/hooks/useShiftOrders";
import { usePermissions } from "@/hooks/usePermissions";
import { HUB_PERMISSIONS } from "@/lib/navigation";
import ShiftStats, { HubStat } from "@/components/hub/ShiftStats";
import NeedsAttention, {
  AttentionGroup,
} from "@/components/hub/NeedsAttention";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowRight,
  Banknote,
  ChefHat,
  CheckCircle2,
  Clock,
  Plus,
  ReceiptText,
  ShoppingCart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface HubTile {
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
  /** Shown when the user holds one of these permissions… */
  permissions?: string[];
  /** …or one of these functional roles (role-gated pages such as /kitchen). */
  roles?: Roles[];
  /** Optional live count badge, keyed by the resolver in HubPage. */
  badgeKey?: "orders" | "kitchen";
}

// Secondary entry points for floor staff (the primary "new order" action is a
// dedicated hero button). On the POS machine the sidebar is hidden by design,
// so this hub gives cashiers and waiters one place to reach everything.
const TILES: HubTile[] = [
  {
    label: "Commandes",
    description: "Suivre et gérer les commandes en cours",
    to: "/orders",
    icon: ShoppingCart,
    // Orders are open to every restaurant employee; the hub itself already
    // requires orders.manage.
    badgeKey: "orders",
  },
  {
    label: "Caisse",
    description: "Session, encaissements et mouvements",
    to: "/register",
    icon: Banknote,
    permissions: ["cash_register.view"],
  },
  {
    label: "Cuisine",
    description: "Voir les commandes à préparer",
    to: "/kitchen",
    icon: ChefHat,
    // /kitchen is gated on the manager/kitchen roles.
    roles: [Roles.manager],
    badgeKey: "kitchen",
  },
];

const HubPage = () => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const roles = currentUser?.roles;

  const { can, canAny } = usePermissions();
  // Gates follow the API permissions, not roles (SFRESTO-165): collecting
  // payments needs payments.capture, the register page cash_register.view.
  const canManageRegister = can("payments.capture");
  const canSeeRegister = can("cash_register.view");
  // The open session endpoint is readable with cash_register.view or the
  // cashier/manager/waiter role.
  const canReadOpenSession =
    canSeeRegister ||
    isGranted([Roles.manager, Roles.cashier, Roles.waiter], roles);
  const visibleTiles = TILES.filter((tile) => {
    if (!tile.permissions && !tile.roles) return true;
    return (
      (tile.permissions ? canAny(...tile.permissions) : false) ||
      (tile.roles ? isGranted(tile.roles, roles) : false)
    );
  });

  const { data: openSession } = useGetOpenSessionQuery(
    { restaurantId: restaurant?.id ?? "" },
    { skip: !restaurant?.id || !canReadOpenSession }
  );
  const hasOpenSession = !!openSession;

  const shift = useShiftOrders(restaurant?.id);

  const firstName = currentUser?.fullName?.split(" ")[0];
  const greeting = new Date().getHours() < 18 ? "Bonjour" : "Bonsoir";

  // Live count badges for the secondary tiles, resolved per role: cashiers
  // chase payments, waiters chase plates to deliver.
  const tileBadges: Record<NonNullable<HubTile["badgeKey"]>, number> = {
    orders: canManageRegister ? shift.unpaid.length : shift.ready.length,
    kitchen: shift.active.length,
  };

  // Stat strip — role-aware so the four numbers always matter to the viewer.
  const stats: HubStat[] = [
    { label: "Commandes du jour", value: shift.ordersToday, icon: ReceiptText },
    canManageRegister
      ? {
          label: "À encaisser",
          value: shift.unpaid.length,
          icon: Banknote,
          tone: shift.unpaid.length > 0 ? "warning" : "default",
        }
      : {
          label: "En préparation",
          value: shift.preparing.length,
          icon: Clock,
        },
    {
      label: "Prêtes à servir",
      value: shift.ready.length,
      icon: CheckCircle2,
      tone: shift.ready.length > 0 ? "success" : "default",
    },
  ];

  // The working set: collect-payment for cashiers, serve-now for everyone.
  const attentionGroups: AttentionGroup[] = [
    ...(canManageRegister
      ? [{ key: "unpaid", label: "À encaisser", orders: shift.unpaid }]
      : [{ key: "waiting", label: "En attente", orders: shift.active }]),
    { key: "ready", label: "Prêtes à servir", orders: shift.ready },
  ];

  const sessionOpenedBy = openSession?.openedBy?.fullName?.split(" ")[0];
  const sessionOpenedAt = openSession?.openedAt
    ? format(new Date(openSession.openedAt), "HH:mm")
    : null;

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.cashier, Roles.waiter]}
        allowedPermissions={HUB_PERMISSIONS}
      >
        <div className="space-y-6">
          <PageTitle
            title={firstName ? `${greeting}, ${firstName}` : "Accueil"}
            description="Accédez rapidement à tout ce dont vous avez besoin pour le service"
          />

          {/* Cash register status — the floor can't take orders without an
              open session, so surface it prominently. */}
          {canReadOpenSession && (
            <div
              className={cn(
                "flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between",
                hasOpenSession
                  ? "border-green-200 bg-green-50"
                  : "border-red-200 bg-red-50"
              )}
            >
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
                    hasOpenSession
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  )}
                >
                  <Banknote className="h-5 w-5" />
                </span>
                <div>
                  <p
                    className={cn(
                      "font-semibold",
                      hasOpenSession ? "text-green-800" : "text-red-800"
                    )}
                  >
                    {hasOpenSession ? "Caisse ouverte" : "Caisse fermée"}
                  </p>
                  <p
                    className={cn(
                      "text-sm",
                      hasOpenSession ? "text-green-700" : "text-red-700"
                    )}
                  >
                    {hasOpenSession
                      ? sessionOpenedBy && sessionOpenedAt
                        ? `Ouverte par ${sessionOpenedBy} depuis ${sessionOpenedAt}`
                        : "Vous pouvez prendre des commandes."
                      : canSeeRegister
                        ? "Ouvrez une session pour commencer à encaisser."
                        : "Demandez à un caissier d'ouvrir la caisse."}
                  </p>
                </div>
              </div>
              {canSeeRegister && (
                <Link
                  to="/register"
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                    hasOpenSession
                      ? "bg-white text-green-800 hover:bg-green-100"
                      : "bg-red-600 text-white hover:bg-red-700"
                  )}
                >
                  {hasOpenSession ? "Gérer la caisse" : "Ouvrir une session"}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          )}

          {/* Shift at a glance */}
          <ShiftStats stats={stats} />

          {/* Primary action + secondary destinations */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/pos?new=1"
              className="group flex items-center gap-4 rounded-xl bg-primary p-5 text-primary-foreground shadow-sm transition-all hover:bg-primary/90 sm:col-span-2 lg:col-span-3"
            >
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/20">
                <Plus className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-lg font-semibold">Nouvelle commande</p>
                <p className="truncate text-sm text-primary-foreground/80">
                  Prendre une commande au comptoir
                </p>
              </div>
              <ArrowRight className="h-5 w-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
            </Link>

            {visibleTiles.map(
              ({ label, description, to, icon: Icon, badgeKey }) => {
                const badge = badgeKey ? tileBadges[badgeKey] : 0;
                return (
                  <Link
                    key={to}
                    to={to}
                    className="group relative flex items-center gap-4 rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/40 hover:shadow-sm"
                  >
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-card-foreground">
                        {label}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">
                        {description}
                      </p>
                    </div>
                    {badge > 0 && (
                      <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
                        {badge}
                      </span>
                    )}
                    <ArrowRight className="h-5 w-5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                  </Link>
                );
              }
            )}
          </div>

          {/* The floor's working set — what needs action right now. */}
          <NeedsAttention groups={attentionGroups} />
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
};

export default HubPage;
